// src/pages/MainPanel/CreateExperiment/SubmitJob.js
import {
  Box,
  Typography,
  TextField,
  FormHelperText,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PropTypes from "prop-types";

import {
  INK_SECONDARY,
  INK_MUTED,
  HAIRLINE,
  SURFACE,
  RADIUS,
  SHADOW_SM,
  FONT,
} from "../../../theme/tokens";

import MainPanelContent, {
  CONTENT_MAX_WIDTH,
} from "../components/MainPanelContent";

import GPUResourceForm from "./GPUResourceForm";
import CPUResourceForm from "./CPUResourceForm";
import InputWithSlider from "./components/ResourceInputSlider";
import ResourceGroupSelector from "./components/ResourceGroupSelector";
import DatasetSelector from "./components/DatasetSelector";
import ImageSelector from "./components/ImageSelector";
import CommandEditor, { parseCommands } from "./components/CommandEditor";
import {
  TabPanel,
  WizardStepper,
  StepHeader,
  SubHeader,
} from "./components/WizardScaffold";

import { buildJobPreviewYaml } from "./components/previewTemplates";
import ConfigPreviewPanel, {
  ConfigUploadFooter,
} from "./components/ExprConfigPreviewPanel";
import { importJobConfig } from "./components/ExprConfigImport";
import {
  formatExperimentConfigValidationErrors,
  formatExperimentSubmissionError,
  submitExperimentConfigYaml,
  validateExperimentConfigYaml,
} from "./components/ExprConfigValidation";
import {
  buildConfigValidationState,
  buildPartialImportMessage,
  buildPreviewValidationProps,
  createConfigValidationState,
  createDefaultCpuConfig,
  createDefaultGpuConfig,
  isConfigValidationCurrent,
  validationStatusMeta,
} from "./components/ExprConfigStates";

import {
  deriveGpuCpuCores,
  getGpuPartitionSpecs,
  getTargetAllocation,
  validateDnsLabel,
  validateCpuConfig,
  validateGpuConfig,
  validateWalltimeHours,
} from "../../../utils/experimentInputValidation";

import { useProfile } from "../../../context/ProfileContext";
import { useDatasets } from "../../../hooks/useDatasets";
import { useResourceAccessLimits } from "../../../hooks/useResourceAccessLimits";

/* -------------------- Wizard scaffolding -------------------- */
const STEPS = [
  { label: "Workflow Image", caption: "Optional base image" },
  { label: "Name & Group", caption: "Identify & route" },
  { label: "Resources", caption: "Compute & runtime" },
  { label: "Datasets", caption: "Optional mounts" },
  { label: "Commands", caption: "What to run" },
];

const createJobDraft = () => ({
  jobLabel: "",
  selectedGroup: null,
  walltimeHours: null,
  gpuConfig: createDefaultGpuConfig(),
  cpuConfig: createDefaultCpuConfig(16),
  commands: "",
  selectedDatasets: [],
  imageRef: "",
});

const DEFAULT_WALLTIME_HOURS = 72;

const SubmitJob = ({ user, onCancel, onSuccess, onGoToDatasets }) => {
  const { profile, fetchProfile } = useProfile();
  const groups = profile?.groups ?? [];
  const { datasets: availableDatasets, error: datasetsError } = useDatasets();

  // Form state
  const [draft, setDraft] = useState(createJobDraft);
  const {
    jobLabel,
    selectedGroup,
    walltimeHours,
    gpuConfig,
    cpuConfig,
    commands,
    selectedDatasets,
    imageRef,
  } = draft;
  const [tab, setTab] = useState(0);
  const [imageRefError, setImageRefError] = useState("");
  const importInputRef = useRef(null);

  // Server interaction state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [importWarnings, setImportWarnings] = useState([]);
  const [importError, setImportError] = useState("");
  const [importSuccess, setImportSuccess] = useState("");
  const [configValidation, setConfigValidation] = useState(
    createConfigValidationState,
  );

  // Resource identity and availability
  const resourceAvailability = useResourceAccessLimits({
    selectedGroup,
    gpuProduct: gpuConfig.gpuProduct,
    cpuConfig,
    gpuConfig,
    isSubmitJob: true,
  });

  // Derived: job type, target allocation, and partition specs
  const jobType = selectedGroup
    ? selectedGroup.name === "CPU"
      ? "cpu"
      : "gpu"
    : null;
  const targetAllocation = useMemo(
    () =>
      getTargetAllocation(
        selectedGroup,
        jobType === "gpu" ? gpuConfig.gpuProduct : null,
      ),
    [selectedGroup, jobType, gpuConfig.gpuProduct],
  );

  // Walltime is derived from the selected partition allocation.
  const maxWalltime =
    targetAllocation?.max_walltime_batch ?? DEFAULT_WALLTIME_HOURS;
  const gpuPartitionSpecs = useMemo(
    () => getGpuPartitionSpecs(selectedGroup, gpuConfig.gpuProduct),
    [selectedGroup, gpuConfig.gpuProduct],
  );

  // Draft mutation helpers
  const patchDraft = useCallback((patch) => {
    setDraft((prev) => ({ ...prev, ...patch }));
  }, []);

  const patchGpuConfig = useCallback((patch) => {
    setDraft((prev) => ({
      ...prev,
      gpuConfig: { ...prev.gpuConfig, ...patch },
    }));
  }, []);

  const patchCpuConfig = useCallback((patch) => {
    setDraft((prev) => ({
      ...prev,
      cpuConfig: { ...prev.cpuConfig, ...patch },
    }));
  }, []);

  const applySelectedGroup = useCallback((group, overrides = {}) => {
    const isGpuGroup = group ? group.name !== "CPU" : false;
    const nextWalltime = overrides.walltimeHours ?? DEFAULT_WALLTIME_HOURS / 2;

    setDraft((prev) => ({
      ...prev,
      selectedGroup: group,
      walltimeHours: nextWalltime,
      gpuConfig: {
        ...createDefaultGpuConfig(),
        ...(isGpuGroup ? (overrides.gpuConfig ?? {}) : {}),
      },
      cpuConfig: {
        ...createDefaultCpuConfig(16),
        ...(!isGpuGroup ? (overrides.cpuConfig ?? {}) : {}),
      },
    }));
  }, []);

  // Profile and default resource-group initialization
  useEffect(() => {
    if (!profile && user?.id) fetchProfile(user.id);
  }, [profile, user, fetchProfile]);

  useEffect(() => {
    if (selectedGroup || !groups?.length) return;
    const cpuGroup = groups.find((group) => group.name === "CPU");
    applySelectedGroup(cpuGroup || groups[0]);
  }, [applySelectedGroup, groups, selectedGroup]);

  const handleLabelChange = (e) => {
    patchDraft({ jobLabel: e.target.value });
  };

  const handleWalltimeChange = (e) => {
    patchDraft({ walltimeHours: e.target.value });
  };

  // Derived form values and local validation
  const jobLabelValidation = useMemo(
    () => validateDnsLabel(jobLabel),
    [jobLabel],
  );
  const { errors: jobLabelErrors, isValid: isJobLabelDNSValid } =
    jobLabelValidation;

  const walltimeError = useMemo(
    () => validateWalltimeHours(walltimeHours, maxWalltime),
    [walltimeHours, maxWalltime],
  );
  const walltimeHasVisibleError = walltimeHours !== null && !!walltimeError;

  const derivedGpuConfig = useMemo(() => {
    return {
      ...gpuConfig,
      cpuCores: deriveGpuCpuCores(gpuConfig.gpuCount, gpuPartitionSpecs),
    };
  }, [gpuConfig, gpuPartitionSpecs]);

  const cpuValidation = useMemo(
    () =>
      validateCpuConfig(cpuConfig, {
        selectedGroup,
        effectiveLimits: resourceAvailability.cpuEffectiveLimits,
      }),
    [cpuConfig, selectedGroup, resourceAvailability.cpuEffectiveLimits],
  );
  const gpuValidation = useMemo(
    () =>
      validateGpuConfig(derivedGpuConfig, {
        selectedGroup,
        selectedGPUProduct: resourceAvailability.accessibleGPUs.find(
          (gpu) => gpu.type === derivedGpuConfig.gpuProduct,
        ),
        accessibleGPUs: resourceAvailability.accessibleGPUs,
        effectiveLimits: resourceAvailability.gpuEffectiveLimits,
      }),
    [
      derivedGpuConfig,
      selectedGroup,
      resourceAvailability.accessibleGPUs,
      resourceAvailability.gpuEffectiveLimits,
    ],
  );

  const previewYaml = useMemo(() => {
    const parsedCommands = parseCommands(commands);
    return buildJobPreviewYaml({
      jobType,
      jobLabel,
      groupName: selectedGroup?.name ?? null,
      imageRef,
      walltimeHours,
      gpuConfig: derivedGpuConfig,
      cpuConfig,
      selectedDatasets,
      commands: parsedCommands,
    });
  }, [
    jobType,
    jobLabel,
    walltimeHours,
    selectedGroup,
    commands,
    selectedDatasets,
    imageRef,
    derivedGpuConfig,
    cpuConfig,
  ]);

  const configValidated = isConfigValidationCurrent(
    configValidation,
    previewYaml,
  );
  const displayedYaml = configValidated
    ? configValidation.canonicalYaml
    : previewYaml;

  // Keep the validation result tied to the exact YAML that was checked.
  useEffect(() => {
    if (
      configValidation.validatedYaml &&
      configValidation.validatedYaml !== previewYaml
    ) {
      setConfigValidation(createConfigValidationState());
      setImportWarnings([]);
      setImportError("");
      setImportSuccess("");
      setSubmitError(null);
    }
  }, [previewYaml, configValidation.validatedYaml]);

  // Expired receipts must return the UI to validation before execution.
  useEffect(() => {
    if (configValidation.status !== "valid") return undefined;
    const expiresAt = Date.parse(configValidation.validationExpiresAt || "");
    const delay = expiresAt - Date.now();
    if (!Number.isFinite(delay) || delay <= 0) {
      setConfigValidation(createConfigValidationState());
      return undefined;
    }
    const timer = setTimeout(
      () => setConfigValidation(createConfigValidationState()),
      delay,
    );
    return () => clearTimeout(timer);
  }, [configValidation.status, configValidation.validationExpiresAt]);

  // Import and backend YAML validation
  const handleImportConfig = async (event) => {
    const [file] = event.target.files || [];
    event.target.value = "";
    if (!file) return;

    setImportWarnings([]);
    setImportError("");
    setImportSuccess("");
    setSubmitError(null);
    setConfigValidation(createConfigValidationState());

    try {
      const yamlText = await file.text();
      const validation = await validateExperimentConfigYaml(yamlText, "job");

      const imported = importJobConfig({
        yamlText,
        groups,
        availableDatasets,
      });
      patchDraft({
        imageRef: imported.values.imageRef,
        jobLabel: imported.values.jobLabel,
        selectedDatasets: imported.values.selectedDatasets,
        commands: imported.values.commands,
        walltimeHours: imported.values.walltimeHours,
        gpuConfig: imported.values.gpuConfig,
        cpuConfig: imported.values.cpuConfig,
      });
      if (imported.values.selectedGroup) {
        applySelectedGroup(imported.values.selectedGroup, {
          walltimeHours: imported.values.walltimeHours,
          gpuConfig: imported.values.gpuConfig ?? undefined,
          cpuConfig: imported.values.cpuConfig ?? undefined,
        });
      }

      setImportWarnings(validation.warnings || []);
      setImportError(
        validation.valid
          ? ""
          : formatExperimentConfigValidationErrors(validation),
      );
      setImportSuccess(
        validation.valid
          ? `Imported config from ${file.name}.`
          : buildPartialImportMessage(file.name),
      );
      setSubmitError(null);
      setTab(0);
    } catch (error) {
      setImportWarnings([]);
      setImportSuccess("");
      setImportError(error.message || "Failed to import config.");
    }
  };

  const handleValidateConfig = async () => {
    setConfigValidation((prev) => ({
      ...prev,
      status: "validating",
      validatedYaml: previewYaml,
    }));
    setImportWarnings([]);
    setImportError("");
    setImportSuccess("");
    setSubmitError(null);

    try {
      const validation = await validateExperimentConfigYaml(previewYaml, "job");
      setImportWarnings(validation.warnings || []);
      const nextValidation = buildConfigValidationState(
        validation,
        previewYaml,
      );
      setConfigValidation(nextValidation);
      if (nextValidation.status !== "valid") {
        setSubmitError(
          validation.valid
            ? "Validation succeeded without a submission receipt. Please validate again."
            : formatExperimentConfigValidationErrors(validation),
        );
      }
    } catch (error) {
      setConfigValidation({
        ...createConfigValidationState(),
        status: "invalid",
        validatedYaml: previewYaml,
      });
      setSubmitError(
        formatExperimentSubmissionError(error, "Failed to validate config."),
      );
    }
  };

  // Stepper state and navigation
  const isImageRefValid = !imageRefError;
  const resourceHasVisibleError =
    jobType === "gpu"
      ? Object.values(gpuValidation.errors).some(Boolean)
      : jobType === "cpu"
        ? Object.values(cpuValidation.errors).some(Boolean)
        : false;

  const canValidateConfig =
    !isSubmitting && configValidation.status !== "validating";

  const hasStepError = (i) => {
    if (i === 0) return !!imageRef.trim() && !isImageRefValid;
    if (i === 1) return !selectedGroup || !isJobLabelDNSValid;
    if (i === 2) return resourceHasVisibleError || walltimeHasVisibleError;
    if (i === 3) return false;
    if (i === 4) return parseCommands(commands).length === 0;
    return false;
  };

  const isStepEnabled = (i) => {
    return i >= 0 && i < STEPS.length;
  };

  const handleTabChange = (_e, nv) => {
    setTab(nv);
  };

  const handleNext = () => {
    setTab((current) => Math.min(STEPS.length - 1, current + 1));
  };

  const handleBack = () => setTab((t) => Math.max(0, t - 1));

  // Execute the exact canonical YAML and receipt returned by the dry run.
  const handleSubmit = async () => {
    if (!configValidated) {
      return setSubmitError("Validate the current config before submitting.");
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await submitExperimentConfigYaml({
        yamlText: configValidation.canonicalYaml,
        intent: "job",
        validationId: configValidation.validationId,
      });
      setShowSuccessDialog(true);
    } catch (error) {
      console.error(error);
      if (error?.data?.valid === false) {
        setImportWarnings(error.data.warnings || []);
      }
      if (error?.status === 409 || error?.status === 422) {
        setConfigValidation(createConfigValidationState());
      }
      setSubmitError(
        formatExperimentSubmissionError(error, "Failed to submit job."),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = configValidated && !isSubmitting;
  const validationMeta =
    validationStatusMeta[configValidation.status] || validationStatusMeta.idle;

  // Render helpers
  const runtimeField = (
    <Box>
      <Alert severity="info" sx={{ mb: 1, py: 0.25 }}>
        <Box component="strong" sx={{ fontWeight: 700 }}>
          Your job will be evicted when it hits the runtime limit.
        </Box>{" "}
        Make sure you implement proper checkpointing, or contact EFabric support
        to reserve resources.
      </Alert>
      <InputWithSlider
        id="runtime-hours"
        label="Runtime (hours)"
        description={`Maximum job duration in hours (1 - ${maxWalltime}).`}
        value={walltimeHours ?? 1}
        onChange={handleWalltimeChange}
        helperText={walltimeHasVisibleError ? walltimeError : ""}
        error={walltimeHasVisibleError}
        disabled={isSubmitting}
        minInput={1}
        maxInput={maxWalltime}
      />
    </Box>
  );

  return (
    <MainPanelContent title="Submit a Job" maxWidth={CONTENT_MAX_WIDTH}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          gap: 3,
          // Stretch children to full width when stacked; top-align in the wide
          // row layout so the two columns keep their natural heights.
          alignItems: { xs: "stretch", lg: "flex-start" },
          width: "100%",
        }}
      >
        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            bgcolor: SURFACE,
            border: `1px solid ${HAIRLINE}`,
            borderRadius: `${RADIUS.md}px`,
            boxShadow: SHADOW_SM,
            overflow: "hidden",
          }}
        >
          {/* Stepper header */}
          <Box sx={{ px: 3, py: 2.5, borderBottom: `1px solid ${HAIRLINE}` }}>
            <WizardStepper
              steps={STEPS}
              active={tab}
              isStepEnabled={isStepEnabled}
              hasStepError={hasStepError}
              onStep={(i) => handleTabChange(null, i)}
            />
          </Box>

          <Box sx={{ p: 3 }}>
            {/* Alerts */}
            {isSubmitting && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Submitting your job to the batch queue. This may take a moment.
              </Alert>
            )}
            {datasetsError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Could not load datasets. You can still submit a job without
                attaching a dataset.
              </Alert>
            )}

            {/* Form */}
            <Box
              component="form"
              noValidate
              autoComplete="off"
              // Match Create a Workspace: fill the available width so the form
              // grows/shrinks with the content area (e.g. when the sidebar is
              // collapsed) rather than staying a fixed width.
              sx={{ width: "100%" }}
              onSubmit={(e) => {
                e.preventDefault();
                if (tab < STEPS.length - 1) handleNext();
              }}
            >
              {/* Step 1 — Workflow image */}
              <TabPanel value={tab} index={0}>
                <StepHeader
                  title="Workflow Image"
                  description="Optionally select a workflow image from the EFabric registry to reproduce a job. Skip this step to use the default image."
                />
                <ImageSelector
                  imageRef={imageRef}
                  onImageRefChange={(value) => patchDraft({ imageRef: value })}
                  onImageRefErrorChange={setImageRefError}
                  disabled={isSubmitting}
                />
              </TabPanel>

              {/* Step 2 — Name & resource group */}
              <TabPanel value={tab} index={1}>
                <StepHeader
                  title="Name & Resource Group"
                  description="Give your job a unique name and choose which resource group to route it through."
                />
                <SubHeader>Job name</SubHeader>
                <Typography
                  sx={{ fontSize: FONT.sm, color: INK_SECONDARY, mb: 2 }}
                >
                  Provide a unique DNS-1123 compliant label. It identifies your
                  job within the EFabric cluster.
                </Typography>
                {!isJobLabelDNSValid && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    A valid job name is required.
                  </Alert>
                )}

                <TextField
                  required
                  fullWidth
                  id="job-label"
                  placeholder="my-job"
                  value={jobLabel}
                  onChange={handleLabelChange}
                  helperText="A unique label for your job (DNS-1123 compliant)."
                  sx={{
                    mb: jobLabelErrors.length || jobLabelErrors.format ? 1 : 3,
                  }}
                  disabled={isSubmitting}
                  error={!!(jobLabelErrors.length || jobLabelErrors.format)}
                />
                {jobLabelErrors.length && (
                  <FormHelperText error sx={{ ml: 2, mt: 0, mb: 2 }}>
                    Must contain no more than 63 characters.
                  </FormHelperText>
                )}
                {jobLabelErrors.format && (
                  <FormHelperText error sx={{ ml: 2, mt: 0, mb: 2 }}>
                    Must contain only lowercase alphanumeric characters or{" "}
                    {`'-'`}. Must start &amp; end with an alphanumeric
                    character.
                  </FormHelperText>
                )}

                <SubHeader sx={{ mt: 4 }}>Resource group</SubHeader>
                <Typography
                  sx={{ fontSize: FONT.sm, color: INK_SECONDARY, mb: 2 }}
                >
                  Select which resource group to route this job through.
                </Typography>
                <ResourceGroupSelector
                  groups={groups || []}
                  selectedGroup={selectedGroup}
                  onGroupChange={(group) => applySelectedGroup(group)}
                  disabled={isSubmitting}
                />
              </TabPanel>

              {/* Step 3 — Resources */}
              <TabPanel value={tab} index={2}>
                <StepHeader
                  title={`Resource Configuration (${(jobType ?? "GPU").toUpperCase()})`}
                  description="Set the runtime and compute resources for your job."
                />

                {jobType === "gpu" ? (
                  <GPUResourceForm
                    value={gpuConfig}
                    errors={gpuValidation.errors}
                    onChange={patchGpuConfig}
                    disabled={isSubmitting}
                    isSubmitJob
                    accessibleGPUs={resourceAvailability.accessibleGPUs}
                    effectiveLimits={resourceAvailability.gpuEffectiveLimits}
                    runtimeField={runtimeField}
                  />
                ) : (
                  <CPUResourceForm
                    effectiveLimits={resourceAvailability.cpuEffectiveLimits}
                    value={cpuConfig}
                    errors={cpuValidation.errors}
                    onChange={patchCpuConfig}
                    disabled={isSubmitting}
                    runtimeField={runtimeField}
                  />
                )}
              </TabPanel>

              {/* Step 4 — Datasets */}
              <TabPanel value={tab} index={3}>
                <StepHeader
                  title="Attach Datasets"
                  description="Optionally mount datasets into your job. You can skip this step."
                />
                <DatasetSelector
                  availableDatasets={availableDatasets}
                  selectedDatasets={selectedDatasets}
                  onSelectedDatasetsChange={(value) =>
                    patchDraft({ selectedDatasets: value })
                  }
                  disabled={isSubmitting}
                  onGoToDatasets={onGoToDatasets}
                />
              </TabPanel>

              {/* Step 5 — Commands */}
              <TabPanel value={tab} index={4}>
                <StepHeader
                  title="Job Commands"
                  description="Specify the commands to run for your job."
                />
                <CommandEditor
                  commands={commands}
                  onCommandsChange={(value) => patchDraft({ commands: value })}
                  disabled={isSubmitting}
                />
              </TabPanel>
            </Box>

            {/* Action bar */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                mt: 4,
                pt: 2.5,
                borderTop: `1px solid ${HAIRLINE}`,
              }}
            >
              {tab === 0 ? (
                <Button
                  variant="outlinedButton"
                  onClick={onCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              ) : (
                <Button
                  variant="outlinedButton"
                  onClick={handleBack}
                  startIcon={<ChevronLeftIcon />}
                  disabled={isSubmitting}
                >
                  Back
                </Button>
              )}

              {tab < 4 ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {tab === 0 && !imageRef.trim() && (
                    <Typography sx={{ fontSize: FONT.xs, color: INK_MUTED }}>
                      No image selected — skip to use the default.
                    </Typography>
                  )}
                  <Button
                    variant="solidButton"
                    onClick={handleNext}
                    endIcon={<ChevronRightIcon />}
                    disabled={isSubmitting}
                  >
                    {tab === 0 && !imageRef.trim() ? "Skip" : "Next"}
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                  >
                    <Box
                      aria-hidden
                      sx={{
                        width: 9,
                        height: 9,
                        borderRadius: "50%",
                        bgcolor: validationMeta.color,
                      }}
                    />
                    <Typography sx={{ fontSize: FONT.md, color: INK_MUTED }}>
                      {validationMeta.label}
                    </Typography>
                  </Box>
                  {!configValidated ? (
                    <Button
                      variant="solidButton"
                      color="primary"
                      onClick={handleValidateConfig}
                      disabled={
                        !canValidateConfig ||
                        configValidation.status === "validating"
                      }
                    >
                      {configValidation.status === "validating" ? (
                        <CircularProgress size={24} sx={{ color: "white" }} />
                      ) : (
                        "Validate"
                      )}
                    </Button>
                  ) : (
                    <Button
                      variant="solidButton"
                      color="primary"
                      onClick={handleSubmit}
                      disabled={!canSubmit}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={24} sx={{ color: "white" }} />
                      ) : (
                        "Submit Job"
                      )}
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        <ConfigPreviewPanel
          yaml={displayedYaml}
          validation={buildPreviewValidationProps({
            primaryError: submitError,
            setPrimaryError: setSubmitError,
            importError,
            setImportError,
            importSuccess,
            setImportSuccess,
            importWarnings,
            setImportWarnings,
          })}
          footer={
            <ConfigUploadFooter
              onUploadClick={() => importInputRef.current?.click()}
            />
          }
        />
        <input
          ref={importInputRef}
          type="file"
          accept=".yaml,.yml,text/yaml"
          hidden
          onChange={handleImportConfig}
        />
      </Box>

      <Dialog
        open={showSuccessDialog}
        onClose={() => {
          setShowSuccessDialog(false);
          onSuccess?.();
        }}
      >
        <DialogTitle>Job Successfully Submitted</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your Job has been submitted to the queue. You can check its status
            in the Jobs section.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowSuccessDialog(false);
              onSuccess?.();
            }}
            variant="solidButton"
          >
            Confirm!
          </Button>
        </DialogActions>
      </Dialog>
    </MainPanelContent>
  );
};

SubmitJob.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  onCancel: PropTypes.func,
  onSuccess: PropTypes.func,
  onGoToDatasets: PropTypes.func,
};

export default SubmitJob;
